import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ButtonEl from '../helpers/ctas/Button'

/**
 * Fetches the authenticated member's context and renders a "Renew membership"
 * button with a pre-filled renewal URL.
 *
 * The URL carries profile fields as query params so the renewals page can
 * pre-populate the D365 form fields without an extra API call:
 *   /member/renewals?contactId=...&providerId=...&fname=...&lname=...
 *                   &email=...&mobile=...&position=...&state=...
 *
 * Only shown when canAccessRenewalForm is true (status is Pending Renewal or
 * Pending Payment and role is Main / Administrator / Finance).
 */
function RenewButton({ memberContextUrl, renewalUrl }) {
  const [memberContext, setMemberContext] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!memberContextUrl) {
      setLoading(false)
      return
    }
    fetch(memberContextUrl, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        setMemberContext(data)
        setLoading(false)
      })
      .catch((err) => {
        console.error('[RenewButton] Failed to load member context:', err)
        setLoading(false)
      })
  }, [memberContextUrl])

  if (loading || !memberContext) return null
  if (!memberContext.canAccessRenewalForm) return null
  // D365 status propagates asynchronously after payment — suppress on the receipt
  // page so the stale canAccessRenewalForm flag doesn't show a redundant Renew CTA.
  if (window.location.pathname.startsWith('/member/receipt')) return null

  const url = buildRenewalUrl(renewalUrl || '/member/renewals', memberContext)

  return (
    <div className="renew-button-wrapper inline-flex">
      <ButtonEl theme="primary" link={{ url, name: 'Renew membership' }} />
    </div>
  )
}

function buildRenewalUrl(base, ctx) {
  const url = new URL(base, window.location.origin)
  const set = (key, val) => { if (val) url.searchParams.set(key, val) }
  set('contactId', ctx.contactId)
  set('providerId', ctx.providerId)
  set('fname', ctx.firstName)
  set('lname', ctx.lastName)
  set('email', ctx.email)
  set('mobile', ctx.mobile)
  set('position', ctx.positionTitle)
  set('state', ctx.state)
  return `${url.pathname}${url.search}`
}

RenewButton.propTypes = {
  memberContextUrl: PropTypes.string,
  renewalUrl: PropTypes.string,
}

export default RenewButton
